import { MutationTree } from 'vuex'
import { RootState, Pawn, Team, Player } from './types'
import { areArraysEqual } from '@/shared/array'
import { uuid } from '@/shared/utils'
import Vue from 'vue'
import { PawnAttribute } from './interfaces'
import { StockEnzyme, StockMolecule } from '@/interfaces/stock'

const mutations: MutationTree<RootState> = {
  setGameIsWonByTeam(state: RootState, value: string) {
    state.gameIsWonByTeam = value
  },
  setBoardX(state: RootState, value: number) {
    state.boardX = value
  },
  setBoardY(state: RootState, value: number) {
    state.boardY = value
  },
  setBoardZoomFactor(state: RootState, value: number) {
    state.boardZoomFactor = value
  },
  initPawns(state: RootState, value: Pawn[]) {
    state.pawns = value
  },
  addPlayer(state: RootState, value: Player) {
    state.players.push(value)
  },
  updatePlayerInfo(state: RootState, { key, info }) {
    let player = state.players[key]
    player = Object.assign(player, info)
  },
  updatePawnAmount(state: RootState, { player, amount }) {
    const index = state.players.findIndex((item: Player) => item.id === player.id) as number
    player.pawnAmount += amount
    state.players.splice(index, 1, player)
  },
  addPawn(state: RootState, { team, player, molecule, chainLengths, doubleBindings }) {
    state.pawns.push({
      id: uuid(),
      teamID: team.id,
      playerID: player.id,
      moleculeID: molecule.id,
      chainLengths,
      doubleBindings,
    })
  },
  removePawn(state: RootState, { player, molecule }) {
    let index = state.pawns.findIndex((pawn: Pawn) => {
      return pawn.playerID === player.id &&
        pawn.moleculeID === molecule.id
    })

    if (index === -1) {
      const teamPlayer = state.players.find((item: Player) => {
        return item.teamID === player.teamID && item.id !== player.id
      }) as Player

      index = state.pawns.findIndex((pawn: Pawn) => {
        return pawn.playerID === teamPlayer.id &&
          pawn.moleculeID === molecule.id
      })
    }

    state.pawns.splice(index, 1)
  },
  removePawnByID(state: RootState, pawnID: string) {
    const index: number = state.pawns.findIndex((pawn: Pawn) => {
      return pawn.id === pawnID
    })

    if (index !== -1) {
      state.pawns.splice(index, 1)
    }
  },
  updatePawn(state: RootState, { id, attributes }) {
    const updatePawn = state.pawns.find((pawn: Pawn) => pawn.id === id)

    if (updatePawn) {
      attributes.forEach((attr: PawnAttribute) => {
        Vue.set(updatePawn, attr.key, attr.value)
      })
    }
  },
  updateTableID(state: RootState, value: number) {
    state.tableID = value
  },
  increaseTurnID(state: RootState) {
    state.turnID = state.turnID + 1

    if (state.turnID % 4 === 0) {
      state.breaths.teamB.hasBreathed = false
    }

    if (state.turnID % 4 === 2) {
      state.breaths.teamA.hasBreathed = false
    }
  },
  setYourTurn(state: RootState, value: boolean) {
    state.yourTurn = value
  },
  updateTeamEnzyme(state: RootState, { team, enzyme, quantity }) {
    const teamEnzyme: StockEnzyme = team.enzymes.find((teamEnzymeItem: StockEnzyme) => {
      return teamEnzymeItem.id === enzyme.id
    }) as StockEnzyme

    teamEnzyme.quantity += quantity

    if (teamEnzyme.quantity < 0) {
      teamEnzyme.quantity = 0
    }
  },
  removePlayerMolecule(state: RootState, { player, modelMolecule, chainLengths, doubleBindings }) {
    const moleculeIndex: number = player.molecules.findIndex((molecule: StockMolecule) => {
      const conditionOutcomes = [molecule.ids.includes(modelMolecule.id)]

      if (chainLengths && chainLengths.length > 0) {
        conditionOutcomes.push(areArraysEqual(molecule.chainLengths, chainLengths))
      }

      if (doubleBindings && doubleBindings.length > 0) {
        conditionOutcomes.push(areArraysEqual(molecule.doubleBindings, doubleBindings))
      }

      return !conditionOutcomes.includes(false)
    })

    if (moleculeIndex >= 0) {
      const playerMolecule: StockMolecule = player.molecules[moleculeIndex]

      playerMolecule.quantity--

      if (playerMolecule.quantity === 0) {
        player.molecules.splice(moleculeIndex, 1)
      }
    }
  },
  addPlayerMolecule(state: RootState, { player, ids, chainLengths, doubleBindings }) {
    const molecule: StockMolecule | undefined = player.molecules.find((playerMolecule: StockMolecule) => {
      return playerMolecule.ids.every((id: string) => ids.includes(id)) &&
        playerMolecule.chainLengths.join('-') === chainLengths.join('-') &&
        playerMolecule.doubleBindings.join('-') === doubleBindings.join('-')
    })

    if (molecule) {
      molecule.quantity++
    } else {
      player.molecules.push({
        ids,
        quantity: 1,
        chainLengths,
        doubleBindings,
      })
    }
  },
  updateState(state: RootState, newState: RootState) {
    state.pawns = newState.pawns
    state.players = newState.players
    state.teams = newState.teams
    state.turnID = newState.turnID
    state.breaths = newState.breaths
    state.gameIsWonByTeam = newState.gameIsWonByTeam
  },
  breathe(state: RootState) {
    const team = state.turnID % 4 < 2 ? 'teamA' : 'teamB'
    const playerID = state.turnID % 4

    if (state.breaths[team].hasBreathed) {
      return
    }

    state.breaths[team].hasBreathed = true
    state.breaths[team].amount++
    state.breaths.playerBreathAmount[playerID]++
  },
}

export default mutations
